import { DatePipe } from '@angular/common';
import { Injectable, TemplateRef } from '@angular/core';
import {
  addCellClassRules,
  AgGridCellPillComponent,
  AgGridCellTemplateRendererComponent,
  AgGridValueFormatters,
  EXCLUDE_COLUMN_FROM_EXPORT,
  LOCKED_COLUMN,
  NULL_DATA_STRING,
  PillColor,
} from '@anduin/shared';
import { CellClassParams, ColDef, ICellRendererParams } from 'ag-grid-community';
import { addValueTooltip } from '../../utils/add-value-tooltip';
import { getFailedReasonCodeFilterOptions } from '../../utils/unsent-email-reason.utils';
import { FailedEmailRow, FailedEmailStatus } from '../../models';

const FAILED_STATUS_COLOR_MAP: Record<FailedEmailStatus, PillColor> = {
  [FailedEmailStatus.FAILED]: 'red',
  [FailedEmailStatus.RETRYING]: 'lavender',
  [FailedEmailStatus.DISMISSED]: 'gray',
  [FailedEmailStatus.RESOLVED]: 'green',
};

const FAILED_STATUS_LABEL_MAP: Record<FailedEmailStatus, string> = {
  [FailedEmailStatus.FAILED]: 'Failed',
  [FailedEmailStatus.RETRYING]: 'Retrying...',
  [FailedEmailStatus.DISMISSED]: 'Dismissed',
  [FailedEmailStatus.RESOLVED]: 'Resolved',
};

@Injectable({ providedIn: 'root' })
export class EmailDeliveryHubFailedColumnBuilder {
  private datePipe = new DatePipe('en-US');

  buildFailedColumns(actionsTemplate: TemplateRef<unknown>, reasonFilterTemplateRef: TemplateRef<unknown>): ColDef<FailedEmailRow>[] {
    return [
      {
        headerName: 'Status',
        field: 'status',
        width: 130,
        sortable: false,
        filter: null,
        cellRenderer: AgGridCellPillComponent,
        cellRendererParams: {
          pill: {
            getValue: ({ value }) => FAILED_STATUS_LABEL_MAP[value] ?? value,
            getColor: ({ value }) => FAILED_STATUS_COLOR_MAP[value] ?? 'gray',
          },
          tooltip: {
            getContent: (params: ICellRendererParams<FailedEmailRow>) => {
              const data = params.data;
              if (!data) return '';
              if (data.status === FailedEmailStatus.DISMISSED && data.dismissedAt) {
                return `Dismissed at ${this.datePipe.transform(data.dismissedAt, 'MM/dd/yyyy hh:mm a')}`;
              }
              if (data.status === FailedEmailStatus.RETRYING && data.retryingSince) {
                return `Retried at ${this.datePipe.transform(data.retryingSince, 'MM/dd/yyyy hh:mm a')}`;
              }
              if (data.status === FailedEmailStatus.RESOLVED && data.resolvedAt) {
                return `Resolved at ${this.datePipe.transform(data.resolvedAt, 'MM/dd/yyyy hh:mm a')}`;
              }
              return '';
            },
          },
        },
        cellClass: addCellClassRules({
          'fst-italic': (params: CellClassParams) => params.value === FailedEmailStatus.RETRYING,
        }),
      },
      {
        headerName: 'Date Received',
        field: 'createdAt',
        flex: 1,
        sort: 'desc',
        valueFormatter: AgGridValueFormatters.DATE(this.datePipe, 'MM/dd/yyyy'),
      },
      {
        headerName: 'Failure Reason',
        field: 'reasonDisplay',
        colId: 'reasonCode',
        flex: 2,
        sortable: false,
        filter: 'customSetFilter',
        filterParams: {
          context: {
            reasonCode: {
              getSetFilterOptions: () => getFailedReasonCodeFilterOptions(reasonFilterTemplateRef),
            },
          },
        },
      },
      { headerName: 'Recommended Action', field: 'recommendedAction', flex: 3, filter: null, sortable: false },
      { headerName: 'Subject', field: 'subject', flex: 2 },
      { headerName: 'Invoice #', field: 'invoiceNumber', width: 110 },
      {
        headerName: 'Recipients',
        field: 'allRecipients',
        flex: 2,
        valueFormatter: ({ value }) => value || NULL_DATA_STRING,
        ...addValueTooltip(true),
      },
      {
        ...LOCKED_COLUMN,
        headerName: '',
        type: EXCLUDE_COLUMN_FROM_EXPORT,
        width: 80,
        minWidth: 80,
        maxWidth: 80,
        pinned: 'right',
        cellRenderer: AgGridCellTemplateRendererComponent,
        cellRendererParams: {
          getTemplate: () => actionsTemplate,
        },
      },
    ];
  }
}
